import { booksToday } from './report-range'
import { ReportSaleKind, SalesSummaryGroupBy } from './types'
import type {
  ReportMoney,
  SalesRegisterReport,
  SalesRegisterRow,
  SalesRegisterTotal,
  SalesSummaryBranch,
  SalesSummaryReport,
  SalesSummaryRow,
} from './types'

/**
 * The register, regrouped.
 *
 * The server answers the sales register as one line per invoice and groups
 * nothing but the sales summary, which it cuts by outlet. The legacy pack read
 * the same invoices by customer, by employee and by outlet — and every one of
 * those is the register's own lines put in blocks, so they are built here from
 * the one read rather than asked of an endpoint that does not exist.
 *
 * Two rules keep the blocks honest against the server's figures:
 *
 *  - Money is added in whole cents. Ten lines of `0.1` summed as floats come to
 *    `0.9999999999999999`, and a subtotal one cent off the period total is a
 *    subtotal nobody trusts again.
 *  - Refunds are counted beside the sales and never netted into them, exactly
 *    as the server's totals do — so the blocks add up to the period total row,
 *    which is still the server's own.
 */

/** Who or where a line belongs to. */
export const SalesDimension = {
  Location: 'location',
  Employee: 'employee',
  Customer: 'customer',
} as const

export type SalesDimension = (typeof SalesDimension)[keyof typeof SalesDimension]

/** The block a line goes in, named as the register names it. */
export function dimensionName(row: SalesRegisterRow, dimension: SalesDimension): string {
  switch (dimension) {
    case SalesDimension.Location:
      return row.branchName || 'Unknown outlet'
    // QR orders are settled by the customer's own phone, so no cashier took the
    // money. Named as such rather than left blank, so the block is still there
    // to be read against the tills.
    case SalesDimension.Employee:
      return row.cashierName || 'No cashier (QR ordering)'
    case SalesDimension.Customer:
      return row.customerName || 'Guest'
  }
}

/** The trading day a line settled on, in the books' zone. */
export function tradingDayOf(at: string): string {
  const instant = new Date(at)

  return Number.isNaN(instant.getTime()) ? at.slice(0, 10) : booksToday(instant)
}

/* -------------------------------------------------------------------------- */
/* Tallies                                                                    */
/* -------------------------------------------------------------------------- */

interface Tally {
  invoices: number
  refundCount: number
  /** Every money field below is in cents. */
  refundAmount: number
  totalAmount: number
  discount: number
  afterDiscount: number
  vat: number
  delivery: number
  net: number
  totalReceived: number
  changeGiven: number
}

function emptyTally(): Tally {
  return {
    invoices: 0,
    refundCount: 0,
    refundAmount: 0,
    totalAmount: 0,
    discount: 0,
    afterDiscount: 0,
    vat: 0,
    delivery: 0,
    net: 0,
    totalReceived: 0,
    changeGiven: 0,
  }
}

function cents(amount: number | null | undefined): number {
  return Math.round((amount ?? 0) * 100)
}

function add(tally: Tally, row: SalesRegisterRow): void {
  if (row.kind === ReportSaleKind.Refund) {
    tally.refundCount += 1
    tally.refundAmount += cents(row.net)

    return
  }

  tally.invoices += 1
  tally.totalAmount += cents(row.totalAmount)
  tally.discount += cents(row.discount)
  tally.afterDiscount += cents(row.afterDiscount)
  tally.vat += cents(row.vat)
  tally.delivery += cents(row.delivery)
  tally.net += cents(row.net)
  tally.totalReceived += cents(row.totalReceived)
  tally.changeGiven += cents(row.changeGiven)
}

function money(tally: Tally): ReportMoney {
  return {
    totalAmount: tally.totalAmount / 100,
    discount: tally.discount / 100,
    afterDiscount: tally.afterDiscount / 100,
    vat: tally.vat / 100,
    delivery: tally.delivery / 100,
    net: tally.net / 100,
  }
}

function asTotal(tally: Tally): SalesRegisterTotal {
  return {
    ...money(tally),
    invoices: tally.invoices,
    totalReceived: tally.totalReceived / 100,
    changeGiven: tally.changeGiven / 100,
    refundCount: tally.refundCount,
    refundAmount: tally.refundAmount / 100,
  }
}

/** Net over invoices, rounded to the cent — what the summary calls an average ticket. */
function averageTicket(netCents: number, invoices: number): number {
  return invoices ? Math.round(netCents / invoices) / 100 : 0
}

function asSummaryRow(
  tally: Tally,
  key: string,
  name: string,
  period: string | null,
): SalesSummaryRow {
  return {
    ...money(tally),
    branchId: key,
    branchName: name,
    period,
    invoices: tally.invoices,
    averageTicket: averageTicket(tally.net, tally.invoices),
    refundCount: tally.refundCount,
    refundAmount: tally.refundAmount / 100,
  }
}

/* -------------------------------------------------------------------------- */
/* The register in blocks                                                     */
/* -------------------------------------------------------------------------- */

export interface RegisterGroups {
  /** Every line, block by block — each block in the order its money was taken. Page this. */
  rows: SalesRegisterRow[]
  nameOf: (row: SalesRegisterRow) => string
  /** Each block's subtotal over ALL its lines, not just the ones on a page. */
  subtotals: Map<string, SalesRegisterTotal>
  /** Every block together — the lines given, which may be a search's matches. */
  total: SalesRegisterTotal
  /** How many blocks there are. */
  count: number
}

/**
 * The register as blocks of one customer, employee or outlet each, biggest
 * block first — the legacy "Sales Register Summary By …".
 */
export function groupRegister(rows: SalesRegisterRow[], dimension: SalesDimension): RegisterGroups {
  const nameOf = (row: SalesRegisterRow) => dimensionName(row, dimension)
  const blocks = new Map<string, { rows: SalesRegisterRow[]; tally: Tally }>()
  const total = emptyTally()

  for (const row of rows) {
    const name = nameOf(row)
    const block = blocks.get(name) ?? { rows: [], tally: emptyTally() }

    block.rows.push(row)
    add(block.tally, row)
    add(total, row)
    blocks.set(name, block)
  }

  const ordered = [...blocks.entries()].sort(biggestFirst)

  return {
    rows: ordered.flatMap(([, block]) => block.rows),
    nameOf,
    subtotals: new Map(ordered.map(([name, block]) => [name, asTotal(block.tally)])),
    total: asTotal(total),
    count: ordered.length,
  }
}

/* -------------------------------------------------------------------------- */
/* The register as a summary                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The register summed per employee or customer, per trading day or month —
 * shaped as the sales summary the server returns, so the same table prints it.
 *
 * `groupBy` null gives one line per block for the whole period: the per-
 * employee totals, or a location summary.
 *
 * The grand total is the SERVER's register total, not a sum of these blocks.
 * They agree to the cent by construction; if they ever did not, the server's
 * figure is the one a manager reconciles against.
 */
export function summariseRegister(
  report: SalesRegisterReport,
  dimension: SalesDimension,
  groupBy: SalesSummaryGroupBy | null,
): SalesSummaryReport {
  const blocks = new Map<string, { periods: Map<string, Tally>; tally: Tally }>()

  for (const row of report.rows) {
    const name = dimensionName(row, dimension)
    const block = blocks.get(name) ?? { periods: new Map<string, Tally>(), tally: emptyTally() }

    add(block.tally, row)

    if (groupBy) {
      const day = tradingDayOf(row.at)
      const period = groupBy === SalesSummaryGroupBy.Month ? day.slice(0, 7) : day
      const tally = block.periods.get(period) ?? emptyTally()

      add(tally, row)
      block.periods.set(period, tally)
    }

    blocks.set(name, block)
  }

  const branches: SalesSummaryBranch[] = [...blocks.entries()]
    .sort(biggestFirst)
    .map(([name, block]) => ({
      branchId: name,
      branchName: name,
      rows: [...block.periods.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([period, tally]) => asSummaryRow(tally, name, name, period)),
      subtotal: asSummaryRow(block.tally, name, name, null),
    }))

  const total = report.total

  return {
    meta: report.meta,
    groupBy: groupBy ?? SalesSummaryGroupBy.Day,
    branches,
    total: {
      ...total,
      branchId: null,
      branchName: '',
      period: null,
      averageTicket: averageTicket(cents(total.net), total.invoices),
    },
  }
}

/** The bigger block first, by what it took; equal blocks alphabetically. */
function biggestFirst(
  [nameA, a]: [string, { tally: Tally }],
  [nameB, b]: [string, { tally: Tally }],
): number {
  return b.tally.net - a.tally.net || nameA.localeCompare(nameB)
}

/* -------------------------------------------------------------------------- */
/* Search                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Whether a line mentions every word of a search — "dara 202" finds Dara's
 * invoice 202. Case and accents are ignored; an empty search matches all.
 */
export function matchesSearch(
  search: string,
  ...fields: (string | number | null | undefined)[]
): boolean {
  const words = normalise(search).split(/\s+/).filter(Boolean)

  if (words.length === 0) return true

  const haystack = normalise(
    fields.filter((field) => field !== null && field !== undefined).join(' '),
  )

  return words.every((word) => haystack.includes(word))
}

function normalise(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

